import { z } from 'zod'
import stationDataConfigApi from '@/api/config/stationDataConfig'
import programApi from '@/api/operations/programApi'

interface StationDeletionReference {
  id: string | number
  name: string
}

export interface StationDeletionImpact {
  programs: StationDeletionReference[]
  microclimates: StationDeletionReference[]
}

const NON_RUNNING_STATES = new Set([
  'inactive',
  'idling',
  'wait schedule',
  'error',
])

export const isStationDeletionBlocked = (station: {
  stateName?: string
  isRunning?: boolean
}): boolean =>
  station.isRunning === true ||
  !NON_RUNNING_STATES.has(station.stateName?.trim().toLowerCase() ?? '')

const INCOMPLETE_IMPACT_CODE = 'STATION_DELETION_IMPACT_UNAVAILABLE'

export class StationDeletionImpactError extends Error {
  public readonly code = INCOMPLETE_IMPACT_CODE

  constructor() {
    super(INCOMPLETE_IMPACT_CODE)
    this.name = 'StationDeletionImpactError'
  }
}

const numericIdSchema = z.number().int().positive().safe()
const referenceSchema = z.object({
  id: z.union([numericIdSchema, z.string().trim().min(1)]),
  name: z.string().trim().min(1),
})
const pageSchema = z.object({
  data: z.array(z.unknown()),
  totalCount: z.number().int().nonnegative().safe(),
  totalPage: z.number().int().nonnegative().safe(),
})

const parseImpactData = <T>(schema: z.ZodType<T>, value: unknown): T => {
  const result = schema.safeParse(value)
  if (!result.success) throw new StationDeletionImpactError()
  return result.data
}

const loadCompleteList = async (
  load: (pageSize: number) => Promise<unknown>,
): Promise<unknown[]> => {
  let page = parseImpactData(pageSchema, await load(0))
  if (page.data.length > page.totalCount) {
    throw new StationDeletionImpactError()
  }
  if (page.data.length < page.totalCount || page.totalPage > 1) {
    page = parseImpactData(pageSchema, await load(page.totalCount))
  }
  if (page.data.length !== page.totalCount || page.totalPage > 1) {
    throw new StationDeletionImpactError()
  }
  return page.data
}

const requireUniqueReferences = <T extends StationDeletionReference>(
  references: T[],
): T[] => {
  if (new Set(references.map(({ id }) => String(id))).size !== references.length) {
    throw new StationDeletionImpactError()
  }
  return references
}

const loadAssignedProgramIds = async (
  interfaceId: number,
  stationId: number,
): Promise<Set<string>> => {
  const stationData = await loadCompleteList(async (PageSize) => {
    const response = await stationDataConfigApi.getStationListByInterface(
      String(interfaceId),
      { interfaceId, PageSize },
    )
    return response.data
  })
  const stations = parseImpactData(
    z.array(z.object({ id: numericIdSchema, programId: z.unknown() })),
    stationData,
  ).filter(({ id }) => id === stationId)
  if (stations.length !== 1) throw new StationDeletionImpactError()

  const programIds = parseImpactData(
    z.array(numericIdSchema),
    stations[0].programId,
  )
  const assignedIds = new Set(programIds.map(String))
  if (assignedIds.size !== programIds.length) {
    throw new StationDeletionImpactError()
  }
  return assignedIds
}

const loadAffectedPrograms = async (
  interfaceId: number,
  stationId: number,
): Promise<StationDeletionReference[]> => {
  const assignedIds = await loadAssignedProgramIds(interfaceId, stationId)
  if (assignedIds.size === 0) return []

  const data = await loadCompleteList(async (PageSize) => {
    const response = await programApi.getById({
      interfaceId,
      PageSize,
    })
    return response.data
  })
  const programs = requireUniqueReferences(
    parseImpactData(z.array(referenceSchema), data),
  ).filter(({ id }) => assignedIds.has(String(id)))
  if (programs.length !== assignedIds.size) {
    throw new StationDeletionImpactError()
  }
  return programs
}

export const loadStationDeletionImpact = async (
  interfaceId: number,
  stationId: number,
  assignedMicroclimate: StationDeletionReference | null | undefined,
): Promise<StationDeletionImpact> => {
  parseImpactData(numericIdSchema, interfaceId)
  parseImpactData(numericIdSchema, stationId)
  const assigned = parseImpactData(referenceSchema.nullable(), assignedMicroclimate)
  const programs = await loadAffectedPrograms(interfaceId, stationId)
  return { programs, microclimates: assigned === null ? [] : [assigned] }
}
